import { useCallback, useEffect, useState } from "react";
import { Check, LoaderCircle, X } from "lucide-react";
import { apiFetchJson } from "../apiClient";
import { DATABASES } from "../utils/databases";
import "./FeedbackReview.css";

const RATING_LABEL = { helpful: "👍 Helpful", partial: "〜 Partial", not_helpful: "👎 Not helpful" };

/** Admin feedback-review queue (RN-326): triage end-user ratings/corrections,
 * approve one into a golden example (admin edits/confirms the answer text
 * first — raw user text never reaches the retrieval-time injection path
 * unreviewed), or dismiss it. Mirrors AdminUsers.jsx's structure/conventions
 * (apiFetchJson, inline confirm-free approve/dismiss actions, ds-* design
 * system primitives for shell, controls, badges and alerts). */
export default function FeedbackReview() {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [databaseFilter, setDatabaseFilter] = useState("");
  const [pendingId, setPendingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editAnswer, setEditAnswer] = useState("");
  const [editQuestion, setEditQuestion] = useState("");

  const [examples, setExamples] = useState([]);
  const [showExamples, setShowExamples] = useState(false);

  const load = useCallback(async (cursor) => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (databaseFilter) params.set("database", databaseFilter);
    if (cursor) params.set("cursor", cursor);
    try {
      const data = await apiFetchJson(`/api/v1/feedback?${params.toString()}`);
      setItems((prev) => (cursor ? [...prev, ...data.items] : data.items));
      setNextCursor(data.next_cursor);
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }, [statusFilter, databaseFilter]);

  useEffect(() => {
    setLoading(true);
    load(null).finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 2500);
    return () => clearTimeout(t);
  }, [success]);

  const loadMore = async () => {
    setLoadingMore(true);
    await load(nextCursor);
    setLoadingMore(false);
  };

  const loadExamples = useCallback(async () => {
    try {
      const qs = databaseFilter ? `?database=${encodeURIComponent(databaseFilter)}` : "";
      setExamples(await apiFetchJson(`/api/v1/feedback/examples${qs}`));
    } catch (err) {
      setError(err.message);
    }
  }, [databaseFilter]);

  useEffect(() => {
    if (showExamples) loadExamples();
  }, [showExamples, loadExamples]);

  const startApprove = (row) => {
    setEditingId(row.id);
    setEditAnswer(row.answer);
    setEditQuestion(row.question);
  };

  const confirmApprove = async (row) => {
    setPendingId(row.id);
    setError("");
    try {
      await apiFetchJson(`/api/v1/feedback/${row.id}/approve`, {
        method: "POST",
        body: JSON.stringify({
          answer: editAnswer,
          question: editQuestion !== row.question ? editQuestion : undefined,
          database: row.database,
        }),
      });
      setSuccess("Approved as golden example");
      setEditingId(null);
      setItems((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingId(null);
    }
  };

  const dismiss = async (row) => {
    setPendingId(row.id);
    setError("");
    try {
      await apiFetchJson(`/api/v1/feedback/${row.id}/dismiss`, { method: "POST" });
      setItems((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingId(null);
    }
  };

  const toggleExampleActive = async (ex) => {
    try {
      await apiFetchJson(
        `/api/v1/feedback/examples/${ex.id}?is_active=${!ex.is_active}`,
        { method: "PATCH" },
      );
      loadExamples();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="ds-page feedback-review">
      <div className="ds-page-header">
        <h2 className="ds-page-title">Answer feedback</h2>
        <p className="ds-page-subtitle">
          Review end-user ratings and corrections. Approving edits the answer into a
          golden example that gets injected into future similar questions.
        </p>
      </div>

      {error && <div className="ds-alert ds-alert--danger">{error}</div>}
      {success && <div className="ds-alert ds-alert--success">{success}</div>}

      <div className="ds-toolbar">
        <select
          className="ds-select"
          aria-label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="dismissed">Dismissed</option>
          <option value="">All statuses</option>
        </select>
        <select
          className="ds-select"
          aria-label="Workspace"
          value={databaseFilter}
          onChange={(e) => setDatabaseFilter(e.target.value)}
        >
          <option value="">All workspaces</option>
          {DATABASES.map((db) => (
            <option key={db} value={db}>{db}</option>
          ))}
        </select>
        <button
          type="button"
          className="ds-btn ds-btn--secondary"
          aria-pressed={showExamples}
          onClick={() => setShowExamples((v) => !v)}
        >
          {showExamples ? "Hide" : "Show"} approved examples
        </button>
      </div>

      {showExamples && (
        <div className="ds-card feedback-examples">
          {examples.length === 0 ? (
            <div className="ds-empty">No approved examples yet.</div>
          ) : (
            examples.map((ex) => (
              <div className="feedback-example-row" key={ex.id}>
                <span className={`ds-badge ${ex.is_active ? "ds-badge--success" : "ds-badge--danger"}`}>
                  {ex.is_active ? "active" : "inactive"}
                </span>
                <span className="ds-mono feedback-example-db">{ex.database || "Baseline"}</span>
                <span className="feedback-example-q" title={ex.question}>{ex.question}</span>
                <button
                  className="ds-btn ds-btn--sm"
                  onClick={() => toggleExampleActive(ex)}
                >
                  {ex.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {loading ? (
        <div className="ds-empty">Loading feedback…</div>
      ) : items.length === 0 ? (
        <div className="ds-card ds-empty">No feedback in this view.</div>
      ) : (
        <div className="feedback-list">
          {items.map((row) => {
            const isPending = pendingId === row.id;
            const isEditing = editingId === row.id;
            return (
              <div className="ds-card feedback-row" key={row.id}>
                <div className="feedback-row-header">
                  <span className="ds-badge">{RATING_LABEL[row.rating] || row.rating}</span>
                  <span className="ds-mono feedback-example-db">{row.database || "Baseline"}</span>
                  <span className="feedback-created">{new Date(row.created_at).toLocaleString()}</span>
                </div>
                <div className="feedback-qa">
                  <div><strong>Q:</strong> {row.question}</div>
                  <div><strong>A:</strong> {row.answer}</div>
                  {row.comment && (
                    <div className="feedback-comment"><strong>Correction:</strong> {row.comment}</div>
                  )}
                </div>
                {row.status === "pending" && (
                  isEditing ? (
                    <div className="ds-field feedback-edit-form">
                      <label className="ds-label">Question</label>
                      <textarea
                        className="ds-textarea" rows={2} value={editQuestion}
                        onChange={(e) => setEditQuestion(e.target.value)}
                      />
                      <label className="ds-label">Answer (edit before approving)</label>
                      <textarea
                        className="ds-textarea" rows={4} value={editAnswer}
                        onChange={(e) => setEditAnswer(e.target.value)}
                      />
                      <div className="feedback-actions">
                        <button
                          className="ds-btn ds-btn--primary ds-btn--sm"
                          disabled={isPending || !editAnswer.trim()}
                          onClick={() => confirmApprove(row)}
                        >
                          {isPending ? <LoaderCircle size={12} className="ds-spin" /> : <Check size={12} />}
                          Confirm approve
                        </button>
                        <button
                          className="ds-btn ds-btn--sm"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="feedback-actions">
                      <button
                        className="ds-btn ds-btn--primary ds-btn--sm"
                        disabled={isPending}
                        onClick={() => startApprove(row)}
                      >
                        <Check size={12} /> Approve
                      </button>
                      <button
                        className="ds-btn ds-btn--danger ds-btn--sm"
                        disabled={isPending}
                        onClick={() => dismiss(row)}
                      >
                        {isPending ? <LoaderCircle size={12} className="ds-spin" /> : <X size={12} />}
                        Dismiss
                      </button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {nextCursor && (
        <button
          type="button"
          className="ds-btn ds-btn--secondary feedback-load-more"
          disabled={loadingMore}
          onClick={loadMore}
        >
          {loadingMore ? <LoaderCircle size={12} className="ds-spin" /> : "Load more"}
        </button>
      )}
    </div>
  );
}
